import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { FillInTheBlankOptionCreateDTO } from '@/models/Answer';
import { FeedbackCreateDTO } from '@/models/Feedback';
import { FillInTheBlankQuestion } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { adapterFillInTheBlankOptions } from '@/utils/questions/adapterAnswersDTO';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionContextField } from '../fields/QuestionContextField';
import { QuestionTitleField } from '../fields/QuestionTitleField';
import { QuestionFormContainer } from '../QuestionFormContainer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import { useFillInTheBlankPreviewQuestion } from '@/data/hook/question/useFillInTheBlankPreviewQuestion';
import BlankTextAreaField from './BlankTextAreaField';
import { countBlanks } from '@/utils/questions/countBlanks';
import FillInTheBlankOptionsSection from './FillInTheBlankOptionsSection';

export type FillInTheBlankFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  question: string;
  options: FillInTheBlankOptionCreateDTO[];
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type FillInTheBlankFormProps = {
  isActive?: boolean;
  question: FillInTheBlankQuestion;
  isLoading?: boolean;
  onSubmit: (data: FillInTheBlankFormFields) => Promise<FillInTheBlankQuestion>;
};

export default function FillInTheBlankForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: FillInTheBlankFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.fillInTheBlankForm',
  });

  const { identifier } = question;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<FillInTheBlankFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      question: question?.question ?? '',
      options: adapterFillInTheBlankOptions(question?.options ?? []),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<FillInTheBlankFormFields>({
    defaultValues,
    shouldFocusError: true,
  });
  const { watch, trigger, control, reset, formState } = methods;

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    reset,
    formState,
    defaultValues,
  });

  const questionValue = useWatch({ control, name: 'question' });
  const blankCount = countBlanks(questionValue ?? '');

  const realTimeValues = watch();
  const previewQuestion = useFillInTheBlankPreviewQuestion(realTimeValues);

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />

          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />
        </div>

        <Divider />

        <BlankTextAreaField />

        <Divider />

        <FillInTheBlankOptionsSection blankCount={blankCount} />

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
